import { logger } from '@shared/logging/logger';

export interface PerformanceMetric {
  name: string;
  durationMs: number;
  tags?: Record<string, string | number | boolean>;
  timestamp: string;
}

export interface ErrorAlert {
  id: string;
  category: 'APP' | 'MANAGER' | 'WEB_SOCKET' | 'DB' | 'SYNC' | 'CRITICAL';
  severity: 'warning' | 'error' | 'fatal';
  message: string;
  timestamp: string;
  details?: Record<string, any>;
  resolved: boolean;
}

class MonitoringService {
  private metrics: PerformanceMetric[] = [];
  private alerts: ErrorAlert[] = [];
  private maxStoredItems = 100;
  private alertListeners = new Set<(alert: ErrorAlert) => void>();

  constructor() {
    this.setupGlobalHandlers();
  }

  /**
   * Monitor unhandled runtime and promise rejection exceptions.
   */
  private setupGlobalHandlers(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('error', (event) => {
      this.captureException('APP', event.error || new Error(event.message), 'fatal', {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason;
      if (!reason) return;

      // Ignore standard cancellation / abort events and empty objects
      if (typeof reason === 'object') {
        if (reason.name === 'AbortError' || reason.code === 20) return;
        if (typeof reason.message === 'string' && (reason.message.includes('aborted') || reason.message.includes('canceled') || reason.message.includes('cancelled'))) return;
        // Check if object is empty or has no actionable details
        const keys = Object.keys(reason);
        if (keys.length === 0 && !(reason instanceof Error)) return;
        if (keys.length === 1 && keys[0] === 'reason' && (!reason.reason || (typeof reason.reason === 'object' && Object.keys(reason.reason).length === 0))) return;
      }

      if (typeof reason === 'string' && (reason.trim() === '' || reason === 'canceled' || reason === 'cancelled')) return;

      const message = reason?.message || (typeof reason === 'string' ? reason : null);
      if (!message) return;

      this.captureException('APP', reason instanceof Error ? reason : new Error(message), 'error', {
        reason: message,
      });
    });
  }

  /**
   * Record metrics (e.g. API load times, render times, socket latency, DB calls).
   */
  public recordMetric(name: string, durationMs: number, tags?: Record<string, string | number | boolean>): void {
    const metric: PerformanceMetric = {
      name,
      durationMs,
      tags,
      timestamp: new Date().toISOString(),
    };

    this.metrics.push(metric);
    if (this.metrics.length > this.maxStoredItems) {
      this.metrics.shift();
    }

    logger.debug('monitoring', `Metric "${name}" recorded in ${durationMs.toFixed(2)}ms`, tags);

    // OpenTelemetry / Datadog exporter placeholder link
    // exportToTelemetryExporter(metric);
  }

  /**
   * Safe centralized router for classifying and alerting errors.
   */
  public captureException(
    category: ErrorAlert['category'],
    error: any,
    severity: ErrorAlert['severity'] = 'error',
    details?: Record<string, any>
  ): string {
    const errorId = `err_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
    
    const alert: ErrorAlert = {
      id: errorId,
      category,
      severity,
      message: error?.message || String(error),
      timestamp: new Date().toISOString(),
      details: {
        ...details,
        code: error?.code,
        status: error?.status,
        stack: error?.stack,
      },
      resolved: false,
    };

    this.alerts.push(alert);
    if (this.alerts.length > this.maxStoredItems) {
      this.alerts.shift();
    }

    // Direct structured logs through the logger
    logger.debug(`monitoring:${category.toLowerCase()}`, `[${severity.toUpperCase()}] ${alert.message}`, details);

    // Fire alert callbacks for interactive displays, warning modals, or sound alerts
    this.alertListeners.forEach((listener) => {
      try {
        listener(alert);
      } catch (err) {
        console.error('[Monitoring] Alert broadcast failure:', err);
      }
    });

    // Sentry / LogRocket link proxy placeholder:
    // Sentry.captureException(error, { tags: { category, severity }, extra: details });

    return errorId;
  }

  public getMetrics(): PerformanceMetric[] {
    return [...this.metrics];
  }

  public getAlerts(): ErrorAlert[] {
    return [...this.alerts];
  }

  public clearAll(): void {
    this.metrics = [];
    this.alerts = [];
  }

  /**
   * Register warning notifications listeners.
   */
  public onAlertTriggered(callback: (alert: ErrorAlert) => void): () => void {
    this.alertListeners.add(callback);
    return () => {
      this.alertListeners.delete(callback);
    };
  }
}

export const monitoringService = new MonitoringService();
export default monitoringService;
